'use client';

import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { ClientAddress } from '@/app/(main)/client-detail/edit-main/components/ClientAdress';
import { RelatedPassengerAdditionalPhonesAndEmails } from '@/app/(main)/clients/components/form/RelatedPassengerAdditionalPhonesAndEmails';
import { MAILING_FIELDS } from '@/app/(main)/clients/create/const/passenger';
import type { CreateClientFormData } from '@/types/clientsApiTypes';

const RELATED_PASSENGER_BASE_PATH = 'related_passengers.0.client';

export const EditRelatedContactInfoForm = () => {
  const { setValue, getValues, watch } = useFormContext<CreateClientFormData>();
  const isSameAsPrimary = watch(`${RELATED_PASSENGER_BASE_PATH}.same_as_primary`) ?? false;

  const onEmptyRelatedAddress = useCallback(() => {
    const relatedAddressPayload: Record<string, string> = {};
    MAILING_FIELDS.forEach((field) => {
      setValue(`${RELATED_PASSENGER_BASE_PATH}.${field}`, '');
      relatedAddressPayload[field] = '';
    });
    setValue(`${RELATED_PASSENGER_BASE_PATH}.same_as_primary`, false, { shouldValidate: true });
  }, [setValue]);

  const setRelatedAddress = useCallback(
    (address: { [key: string]: string }) => {
      setValue(`${RELATED_PASSENGER_BASE_PATH}.client_mailing_address`, address.client_mailing_address, {
        shouldValidate: true,
      });
      setValue(`${RELATED_PASSENGER_BASE_PATH}.client_mailing_address_line_2`, address.client_mailing_address_line_2, {
        shouldValidate: true,
      });
      setValue(`${RELATED_PASSENGER_BASE_PATH}.client_mailing_city`, address.client_mailing_city, {
        shouldValidate: true,
      });
      setValue(`${RELATED_PASSENGER_BASE_PATH}.client_mailing_state`, address.client_mailing_state ?? null, {
        shouldValidate: true,
      });
      setValue(`${RELATED_PASSENGER_BASE_PATH}.client_mailing_country`, address.client_mailing_country ?? null, {
        shouldValidate: true,
      });
      setValue(`${RELATED_PASSENGER_BASE_PATH}.zip_code`, address.zip_code, { shouldValidate: true });
    },
    [setValue]
  );

  const handleSetRelatedAddress = useCallback(() => {
    const primaryClient = getValues('primary_passenger.client');
    setRelatedAddress({
      client_mailing_address: primaryClient.client_mailing_address ?? '',
      client_mailing_address_line_2: primaryClient.client_mailing_address_line_2 ?? '',
      client_mailing_city: primaryClient.client_mailing_city ?? '',
      client_mailing_state: primaryClient.client_mailing_state ?? '',
      client_mailing_country: primaryClient.client_mailing_country ?? '',
      zip_code: primaryClient.zip_code ?? '',
    });
  }, [setRelatedAddress, getValues]);

  const handleChangeSameAsPrimary = (checked: boolean) => {
    setValue(`${RELATED_PASSENGER_BASE_PATH}.same_as_primary`, checked, { shouldValidate: true });
    if (checked) {
      handleSetRelatedAddress();
    } else {
      onEmptyRelatedAddress();
    }
  };

  return (
    <div className="flex flex-col gap-6 max-md:gap-4">
      <div>
        <div className="flex flex-col gap-[2px] mb-4">
          <p className="text-body-semibold text-black !text-lg">Contact Details</p>
          <p className="text-body-small text-grey-700">You can add multiple email addresses and phone numbers.</p>
        </div>
        <RelatedPassengerAdditionalPhonesAndEmails fieldPrefix="primary_passenger.client" isChild={false} />
      </div>

      <div className="border-t border-grey-300 pt-5 max-md:pt-4">
        <p className="text-body-medium font-semibold text-black pb-4">Mailing Address</p>
        <ClientAddress
          toggleConfig={{
            label: 'Same as primary passenger',
            visible: true,
            value: !!isSameAsPrimary,
            onChange: () => handleChangeSameAsPrimary(!isSameAsPrimary),
          }}
          fields={{
            address_line1: {
              label: 'Address Line 1',
              placeholder: 'Street address, P.O. box',
              name: 'related_passengers.0.client.client_mailing_address',
            },
            address_line2: {
              label: 'Address Line 2',
              placeholder: 'Apartment, suite, unit, building',
              name: 'related_passengers.0.client.client_mailing_address_line_2',
            },
            city: {
              label: 'City',
              placeholder: 'Enter city',
              name: 'related_passengers.0.client.client_mailing_city',
            },
            state: {
              label: 'State/Province',
              placeholder: 'Select state',
              name: 'related_passengers.0.client.client_mailing_state',
            },
            zip: {
              label: 'Zip Code',
              placeholder: 'Enter ZIP or postal code',
              name: 'related_passengers.0.client.zip_code',
            },
            country: {
              label: 'Country',
              placeholder: 'Select country',
              name: 'related_passengers.0.client.client_mailing_country',
            },
          }}
        />
      </div>
    </div>
  );
};
